import axios from "axios";

import {
  setUserCredentials,
  getUserCredentials,
  setCachedFirebaseToken,
} from "../utils/common";
import { API_URL_AUTH } from "../utils/Constant";

/**
 * Login function that calls backend /login/ API
 * @param {string} email user email
 * @param {string} password user password
 * @param {boolean} rememberMe (always uses sessionStorage)
 * @param {Object} appContext app context
 * @param {Function} callback callback function
 */
export const authenticateCredentials = async (email, password) => {
  try {
    const response = await axios.post(`${API_URL_AUTH}/login/`, {
      user_email: email,
      password,
    });

    const { message, result } = response.data;

    if (message === "Success" && result) {
      return { success: true, data: result };
    }

    return {
      success: false,
      message: message || "Login failed",
    };
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    return {
      success: false,
      message,
    };
  }
};

export const signIn = async (email, password, rememberMe, appContext, callback) => {
  try {
    const response = await axios.post(`${API_URL_AUTH}/login/`, {
      user_email: email,
      password,
    });

    const { message, result } = response.data;

    if (message === "Success" && result) {
      const data = { ...result };
      setUserCredentials("session", data, appContext);
      callback({ success: true, data });
    } else {
      callback({
        success: false,
        message: message || "Login failed",
      });
    }
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    callback({
      success: false,
      message,
    });
  }
};

/**
 * Refresh Firebase token using refresh token
 * @returns {Object} { success, firebase_id_token, firebase_refresh_token, expires_in, message }
 */
export const refreshToken = async () => {
  try {
    const userCredentials = getUserCredentials();
    if (!userCredentials?.firebase_refresh_token) {
      return {
        success: false,
        message: "No refresh token available",
      };
    }

    const response = await axios.post(
      `${API_URL_AUTH}/login/refresh/`,
      {
        refresh_token: userCredentials.firebase_refresh_token,
        disableInterceptor: true, // Bypass interceptor to avoid adding expired token and user payload
      },
    );

    const { message, result } = response.data;

    if (message === "Success" && result) {
      const { firebase_id_token, firebase_refresh_token, expires_in } = result;

      // Update tokens in storage
      const updatedCredentials = {
        ...userCredentials,
        firebase_id_token,
        firebase_refresh_token,
        expires_in: String(expires_in),
      };

      // const storage = userCredentials.rememberMe ? localStorage : sessionStorage;
      const storage = sessionStorage;
      storage.setItem("user_details", JSON.stringify(updatedCredentials));

      setCachedFirebaseToken(firebase_id_token);

      return {
        success: true,
        firebase_id_token,
        firebase_refresh_token,
        expires_in: String(expires_in),
      };
    }

    return {
      success: false,
      message: message || "Token refresh failed",
    };
  } catch (e) {
    const errorDetail = e.response?.data?.detail;
    const errorMessage = e.response?.data?.message;
    const isTokenExpired = errorDetail === "Token has expired." || errorMessage === "Token has expired.";

    const message = errorMessage ?? errorDetail ?? e.message ?? "Token refresh failed";

    // If refresh token itself is expired, return a specific error
    if (isTokenExpired) {
      return {
        success: false,
        message: "Refresh token has expired. Please log in again.",
        refreshTokenExpired: true,
      };
    }

    return {
      success: false,
      message,
      refreshTokenExpired: false,
    };
  }
};

export const sendForgotPasswordEmail = async (email, callback) => {
  try {
    const response = await axios.post(
      `${API_URL_AUTH}/forgotpassword/`,
      {
        email,
      },
    );

    const { message } = response.data;

    if (message === "Password reset email sent") {
      callback({ success: true, message });
    } else {
      callback({
        success: false,
        message: message || "Something went wrong",
      });
    }
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    callback({
      success: false,
      message,
    });
  }
};

export const changePwd = async (oldPassword, newPassword, appContext, callback) => {
  try {
    const response = await axios.post(`${API_URL_AUTH}/changepassword/`, {
      old_password: oldPassword,
      new_password: newPassword,
    });

    const { message, result } = response.data || {};
    const isSuccess = message === "Success" || message === "Password changed successfully";

    if (isSuccess) {
      if (result) {
        const {
          firebase_id_token: newIdToken,
          firebase_refresh_token: newRefreshToken,
          expires_in: expiresIn,
        } = result;
        const currentCredentials = getUserCredentials();

        if (currentCredentials) {
          const updatedCredentials = {
            ...currentCredentials,
            firebase_id_token: newIdToken ?? currentCredentials.firebase_id_token,
            firebase_refresh_token: newRefreshToken ?? currentCredentials.firebase_refresh_token,
            expires_in: expiresIn ? String(expiresIn) : currentCredentials.expires_in,
          };

          // const storageType = currentCredentials.rememberMe ? "local" : "session";
          const storageType = "session";

          if (appContext) {
            setUserCredentials(storageType, updatedCredentials, appContext);
          } else {
            // const storage = currentCredentials.rememberMe ? localStorage : sessionStorage;
            const storage = sessionStorage;
            storage.setItem("user_details", JSON.stringify(updatedCredentials));
          }

          const tokenToCache = newIdToken ?? updatedCredentials.firebase_id_token ?? null;
          setCachedFirebaseToken(tokenToCache);
        }
      }

      const responsePayload = {
        success: true,
        message: message || "Password changed successfully",
      };

      if (result) {
        responsePayload.result = result;
      }

      callback(responsePayload);
    } else {
      callback({
        success: false,
        message: message || "Something went wrong",
      });
    }
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    callback({
      success: false,
      message,
    });
  }
};

/**
 * Reset password using oobCode from email link
 * @param {string} oobCode verification code from email link
 * @param {string} newPassword new password to set
 * @param {Function} callback callback function
 */
export const resetPassword = async (oobCode, newPassword, callback) => {
  try {
    const response = await axios.post(`${API_URL_AUTH}/resetpassword/`, {
      oobCode,
      newPassword,
    });

    const { message, email, requestType } = response.data;

    if (requestType === "PASSWORD_RESET" || message === "Success") {
      callback({ success: true, email, message: message || "Password reset successfully" });
    } else {
      callback({
        success: false,
        message: message || "Something went wrong",
      });
    }
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    callback({
      success: false,
      message,
    });
  }
};

/**
 * Authenticate support credentials (no company selected).
 * @param {string} email user email
 * @param {string} password user password
 * @returns {Object} { success, data?, message? }
 */
export const authenticateSupportCredentials = async (email, password) => {
  try {
    const result = await axios.post(`${API_URL_AUTH}/supportlogin/`, {
      user_email: email,
      password,
    });

    if (result.data.message === "Success") {
      const {
        user_email: userEmail,
        firebase_id_token: firebaseIdToken,
        firebase_refresh_token: firebaseRefreshToken,
        expires_in: expiresIn,
      } = result.data.result;

      return {
        success: true,
        data: {
          user_email: userEmail,
          firebase_id_token: firebaseIdToken,
          firebase_refresh_token: firebaseRefreshToken,
          expires_in: expiresIn,
        },
      };
    }

    return {
      success: false,
      message: result.data.message || "Login failed",
    };
  } catch (e) {
    const message = e.response?.data?.message ?? e.message ?? "Something went wrong";
    return {
      success: false,
      message,
    };
  }
};

/**
 * Fetch list of companies grouped by domain for support login dropdown.
 * @param {string} firebaseIdToken Bearer token from the initial /supportlogin/ call
 * @param {string} userEmail support user email from the initial /supportlogin/ call
 * @returns {Object} { success, data }
 */
export const fetchSupportCompanies = async (firebaseIdToken, userEmail) => {
  try {
    if (!firebaseIdToken) {
      return { success: false, data: "Authentication required to load companies." };
    }
    if (!userEmail) {
      return { success: false, data: "User email is required to load companies." };
    }

    const result = await axios.get(`${API_URL_AUTH}/getcompanieslist/`, {
      params: { user_email: userEmail },
      headers: { Authorization: `Bearer ${firebaseIdToken}` },
    });

    if (result.data.message === "Success") {
      return {
        success: true,
        data: result.data.result,
      };
    }
    return { success: false, data: result.data.message };
  } catch (e) {
    const { response } = e;
    return { success: false, data: response?.data?.message || "Something went wrong." };
  }
};

/**
 * Map company list response to Ant Design Select grouped options.
 * @param {Array} companyList result from fetchSupportCompanies
 * @returns {Array} grouped select options
 */
export const formatSupportCompanyOptions = (companyList) => {
  if (!Array.isArray(companyList)) {
    return [];
  }
  return companyList.map(({
    company_domain,
    company_name: companies,
    admin_user_payload: adminPayloads,
  }) => ({
    label: company_domain,
    options: companies.map((company_name) => {
      const payloadEntry = Array.isArray(adminPayloads)
        ? adminPayloads.find((item) => item.company_name === company_name)
        : null;
      return {
        label: company_name,
        value: `${company_name}${company_domain}`,
        company_domain,
        user_payload: payloadEntry?.user_payload,
      };
    }),
  }));
};

/**
 * Build support session from final /supportlogin/ tokens plus selected company.
 * Final /supportlogin/ only returns tokens; company fields come from /getcompanieslist/.
 * @param {Object} apiData tokens from final /supportlogin/ result
 * @param {string} companyName selected company name
 * @param {string} companyDomain selected company domain
 * @param {string} userPayload user_payload from /getcompanieslist/ for selected company
 * @returns {Object} session fields
 */
export const mapSupportLoginSession = (apiData, companyName, companyDomain, userPayload) => {
  const {
    user_email,
    firebase_id_token,
    firebase_refresh_token,
    expires_in,
  } = apiData;

  return {
    user_email,
    company_name: companyName,
    company_domain: companyDomain,
    user_payload: userPayload,
    firebase_id_token,
    firebase_refresh_token,
    expires_in,
  };
};

/**
 * Complete support login for a selected company.
 * @param {string} email user email
 * @param {string} password password
 * @param {String} companyName company name
 * @param {String} companyDomain company domain name
 * @param {string} firebaseIdToken Bearer token from the first /supportlogin/ call
 * @returns {Object} { success, data }
 */
export const esvSupportLogin = async (email, password, companyName, companyDomain, firebaseIdToken) => {
  try {
    const payload = {
      user_email: email,
      password,
      company_name: companyName,
      company_domain: companyDomain,
    };
    const config = {};
    if (firebaseIdToken) {
      config.headers = { Authorization: `Bearer ${firebaseIdToken}` };
    }
    const result = await axios.post(`${API_URL_AUTH}/supportlogin/`, payload, config);

    if (result.data.message === "Success") {
      return {
        success: true,
        data: result.data.result,
      };
    }
    return { success: false, data: result.data.message };
  } catch (e) {
    const { response } = e;
    return { success: false, data: response?.data?.message || "Something went wrong." };
  }
};
